import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { SNAPPY_SPRING } from './motion';
import { tabId, tabPanelId } from '../common/TabNavigation';

export interface PortalTabDefinition<K extends string> {
  key: K;
  label: string;
  icon?: ReactNode;
}

interface PortalTabsProps<K extends string> {
  tabs: ReadonlyArray<PortalTabDefinition<K>>;
  activeTab: K;
  onTabChange: (tab: K) => void;
  idPrefix: string;
  ariaLabel: string;
  /** Right-aligned content on the tab row (e.g. "2 required fields left"). */
  trailing?: ReactNode;
  className?: string;
}

/** Underlined tab strip from the Store Listing design (icon + bold label). */
export function PortalTabs<K extends string>({
  tabs,
  activeTab,
  onTabChange,
  idPrefix,
  ariaLabel,
  trailing,
  className,
}: PortalTabsProps<K>) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-seeker-800 px-4 sm:px-[60px]',
        className,
      )}
    >
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="scrollbar-hide -mb-px flex items-center gap-3 overflow-x-auto"
      >
        {tabs.map(({ key, label, icon }) => {
          const isActive = key === activeTab;
          return (
            <button
              type="button"
              key={key}
              id={tabId(idPrefix, key)}
              role="tab"
              aria-selected={isActive}
              aria-controls={tabPanelId(idPrefix, key)}
              onClick={() => onTabChange(key)}
              className={cn(
                'relative flex h-16 shrink-0 items-center gap-1 px-4 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70 sm:px-8 [&>svg]:size-4 [&>svg]:shrink-0',
                isActive
                  ? 'text-white'
                  : 'text-seeker-500 hover:text-seeker-300',
              )}
            >
              {icon}
              <span className="text-sm font-bold">{label}</span>
              {isActive ? (
                <motion.span
                  layoutId={`${idPrefix}-underline`}
                  transition={SNAPPY_SPRING}
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-0 h-0.5 bg-seeker-green"
                />
              ) : null}
            </button>
          );
        })}
      </div>
      {trailing ? (
        <div className="flex items-center py-3 text-sm">{trailing}</div>
      ) : null}
    </div>
  );
}
